import type { ServiceConfigsValues } from '../../common/constants'
import { type FilesApi,FileScope, type FileScopes } from '../../common/context-files'
import type { Extension } from '../extension'
import type { OAuthBag } from '../oauth/types'
import type { HandlerBag, Logger } from '../types'
import { defaultLogger } from './logger'
import { createThrowingProxy } from './proxies'

// One invocation's resolved surroundings, and the `bag` handlers see (07-actions.md): the resolved `config`,
// the `initContext` return as `context`, `oauth`, the bound `apiRequest`, `files`, `filesScope`, `logger`,
// and `helpers`.
//
// Everything is derived LAZILY and memoized per instance. That matters because the OAuth callback needs a
// second context bound to the freshly-obtained token (`withAccessToken`), and used to rebuild the whole bag
// from scratch each time it asked — resolving config, running `initContext` and re-running the `helpers`
// factory up to three times per callback. Now each context resolves each of those once, and a differently
// tokened context is an explicit second instance rather than an implicit rebuild.
//
// `toBag()` returns a PLAIN OBJECT. Author code never receives a class instance — that is the frozen
// handler contract.

export interface ExecutionContextOptions {
  configs?: ServiceConfigsValues
  accessToken?: string
  logger?: Logger
  // Provisioned by the caller only when the extension declares `usesFileStorage`; otherwise the bag's
  // `files` slot is a proxy that throws on any access.
  files?: unknown
}

export class ExecutionContext {
  readonly #extension: Extension
  readonly #options: ExecutionContextOptions

  #config?: Record<string, unknown>
  #context?: unknown
  #oauth?: OAuthBag
  #apiRequest?: HandlerBag['apiRequest']
  #bag?: HandlerBag

  constructor(extension: Extension, options: ExecutionContextOptions = {}) {
    this.#extension = extension
    this.#options = options
  }

  get logger(): Logger {
    return this.#options.logger ?? defaultLogger
  }

  get config(): Record<string, unknown> {
    this.#config ??= this.#extension.config.resolve(this.#options.configs)

    return this.#config
  }

  // The header access token plus its deserialized composite payload — or a throwing proxy when the
  // extension never declared oauth, so accidental use fails loudly at the call site.
  get oauth(): OAuthBag {
    this.#oauth ??= this.#extension.oauth
      ? this.#extension.oauth.buildBag(this.#options.accessToken)
      : (createThrowingProxy('oauth', 'declare ext.setupOauth2(...) first') as OAuthBag)

    return this.#oauth
  }

  // The scope names, so a handler writes `filesScope.WORKSPACE` instead of the bare string. Deliberately NOT
  // gated on `usesFileStorage`: it is a set of constants, not a capability, and it is useful for building a
  // scope value to pass around even where no file call happens. Reaching for `files` without the opt-in still
  // fails loudly on its own.
  get filesScope(): FileScopes {
    return FileScope
  }

  // The proxy stands in for the real client and throws on ANY access, so handlers see one type either way.
  get files(): FilesApi {
    return (this.#props.usesFileStorage && this.#options.files
      ? this.#options.files
      : createThrowingProxy('files', 'set usesFileStorage:true in createExtension first')) as FilesApi
  }

  get context(): unknown {
    this.#context ??= this.#props.initContext
      ? this.#props.initContext({ config: this.config, oauth: this.oauth })
      : {}

    return this.#context
  }

  // The author's own `apiRequest`, with `context` + `logger` injected so handlers don't pass them. Both are
  // read at CALL time, off this context.
  get apiRequest(): HandlerBag['apiRequest'] {
    if (!this.#props.apiRequest) {
      return undefined as unknown as HandlerBag['apiRequest']
    }

    this.#apiRequest ??= ((arg: Record<string, unknown>) =>
      (this.#props.apiRequest as (a: unknown) => unknown)({
        ...arg,
        context: this.context,
        logger: this.logger,
      })) as HandlerBag['apiRequest']

    return this.#apiRequest
  }

  // The bag every handler receives. Memoized: one bag per context, so the `helpers` factory runs once.
  toBag(): HandlerBag {
    if (this.#bag) {
      return this.#bag
    }

    const bag = {
      config: this.config,
      context: this.context,
      oauth: this.oauth,
      apiRequest: this.apiRequest,
      files: this.files,
      filesScope: this.filesScope,
      logger: this.logger,
    } as HandlerBag

    // Published before `helpers` is attached: the factory receives the bag it is about to complete, and
    // anything it captures sees the finished object.
    this.#bag = bag
    bag.helpers = this.#props.helpers ? this.#props.helpers(bag) : undefined

    return bag
  }

  // A handler's bag plus whatever its kind adds — `params`, `criteria`, `providerEventData`, … Always a
  // fresh object, so a handler cannot mutate the shared bag.
  bagWith<T extends object>(extra: T): HandlerBag & T {
    return { ...this.toBag(), ...extra }
  }

  // A sibling context bound to a different access token — the OAuth callback runs its identity lookup
  // against the token it just obtained. Everything else (config source, files client, logger) is shared.
  withAccessToken(accessToken?: string): ExecutionContext {
    return new ExecutionContext(this.#extension, { ...this.#options, accessToken })
  }

  get #props() {
    return this.#extension.props
  }
}
